import {
  Box,
  Card,
  FormControlSizes,
  HStack,
  MixpanelEvent,
  MixpanelEventProperty,
  SymbolDisplay,
  SymbolDisplaySize,
  Tab,
  TabList,
  TabSize,
  Tabs,
  ToggleGroup,
  UnifiedLiquidityEnum,
  useMixpanel,
  type Order,
} from '@talos/kyoko';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FilledMarketDistributionChart } from '../FilledMarketDistributionChart';
import { OpenMarketDistributionChart } from '../OpenMarketDistributionChart';
import { SymbolDistributionChart } from '../SymbolDistributionChart';
import { MarketDistributionLegEnum } from '../types';

const MarketDistributionTabNames = ['Filled', 'Open', 'Symbols'] as const;
type MarketDistributionTabName = (typeof MarketDistributionTabNames)[number];

export interface MarketDistributionChartProps {
  order: Order;
  unifiedLiquidity: UnifiedLiquidityEnum;
  isMultileg: boolean;
}

export const MarketDistributionCard = memo(function MarketDistributionTabs({
  unifiedLiquidity,
  isMultileg,
  order,
}: MarketDistributionChartProps) {
  const isUnifiedLiquidity = unifiedLiquidity === UnifiedLiquidityEnum.Enabled;
  const [activeTab, setActiveTab] = useState(0);
  const [activeLeg, setActiveLeg] = useState<0 | 1 | undefined>(isMultileg && !isUnifiedLiquidity ? 0 : undefined);
  const mixpanel = useMixpanel();

  useEffect(() => {
    if (!isMultileg) {
      setActiveLeg(undefined);
      return;
    }

    if (!isUnifiedLiquidity && activeLeg == null) {
      setActiveLeg(0);
    }
  }, [activeLeg, isMultileg, isUnifiedLiquidity]);

  const handleTabChange = useCallback(
    (tabIndex: number) => {
      setActiveTab(tabIndex);
      mixpanel.track(MixpanelEvent.ChangeMarketDistributionTab, {
        [MixpanelEventProperty.TabLabel]: MarketDistributionTabNames[tabIndex],
      });
    },
    [mixpanel]
  );

  const tabs: MarketDistributionTabName[] = useMemo(() => {
    if (isUnifiedLiquidity) {
      return [...MarketDistributionTabNames];
    } else {
      return MarketDistributionTabNames.filter(n => n !== 'Symbols');
    }
  }, [isUnifiedLiquidity]);

  const activeTabName = tabs[activeTab];

  const actions = useMemo(() => {
    const legOptions = [
      ...(isUnifiedLiquidity ? [{ value: MarketDistributionLegEnum.Order, displayName: 'Order' }] : []),
      { value: MarketDistributionLegEnum.Leg1, displayName: 'Leg 1' },
      { value: MarketDistributionLegEnum.Leg2, displayName: 'Leg 2' },
    ];

    const legValue =
      activeLeg == null
        ? MarketDistributionLegEnum.Order
        : activeLeg === 0
          ? MarketDistributionLegEnum.Leg1
          : MarketDistributionLegEnum.Leg2;

    return (
      isMultileg && (
        <ToggleGroup<MarketDistributionLegEnum>
          options={legOptions}
          value={legValue}
          onChange={next => {
            if (next === MarketDistributionLegEnum.Order) {
              setActiveLeg(undefined);
              return;
            }
            setActiveLeg(next === MarketDistributionLegEnum.Leg1 ? 0 : 1);
          }}
          size={FormControlSizes.Small}
        />
      )
    );
  }, [activeLeg, isMultileg, isUnifiedLiquidity]);

  const header = useMemo(() => {
    return (
      <Tabs
        m="-spacing12"
        px="spacing12"
        pb="spacing12"
        flex="0 0 auto"
        mr="0"
        w="calc(100% + 24px)"
        selectedIndex={activeTab}
        onSelect={handleTabChange}
        size={TabSize.Large}
      >
        <TabList suppressOverflowList={true} w="100%" isBordered={true} rightItems={actions}>
          {tabs.map(name => (
            <Tab label={name} key={name} />
          ))}
        </TabList>
      </Tabs>
    );
  }, [activeTab, handleTabChange, tabs, actions]);

  return (
    <Card px={0} pb={0}>
      {header}
      <TabContent activeTabName={activeTabName} order={order} isMultileg={isMultileg} activeLeg={activeLeg} />
    </Card>
  );
});

const TabContent = ({
  activeTabName,
  activeLeg,
  order,
  isMultileg,
}: {
  activeTabName: MarketDistributionTabName;
  order: Order;
  isMultileg: boolean;
  activeLeg: 0 | 1 | undefined;
}) => {
  const symbol = activeLeg != null ? order?.LegSummary?.find(leg => leg.LegIndex === activeLeg)?.Symbol : order.Symbol;

  return (
    <Box w="100%" h="100%">
      {isMultileg && (
        <HStack justifyContent="space-between" p="spacing12">
          <SymbolDisplay symbol={symbol} showDescription={false} p="0" size={SymbolDisplaySize.Small} />
        </HStack>
      )}
      {activeTabName === 'Filled' && <FilledMarketDistributionChart order={order} legIndex={activeLeg} />}
      {activeTabName === 'Open' && <OpenMarketDistributionChart order={order} legIndex={activeLeg} />}
      {activeTabName === 'Symbols' && <SymbolDistributionChart order={order} legIndex={activeLeg} />}
    </Box>
  );
};
